import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { AxiosError } from 'axios'
import { QueryClientProvider } from '@tanstack/react-query'
import {
  CLARITY_ARTEM_ADMIN_KEY,
  CLARITY_USER_ID_STORAGE_KEY,
} from '@/shared/clarity-session-storage-keys'
import { USER_SESSION_QUERY_KEY } from '@/shared/hooks/api/auth'
import '@/shared/i18n'
import * as Sentry from '@sentry/react'
import { clarity } from 'react-microsoft-clarity'
import { createBrowserRouter, RouterProvider } from 'react-router'
import { createQueryClientWithFailureReporting } from '@/shared/lib/createQueryClientWithFailureReporting'
import { initBrowserSentry } from '@/shared/lib/initBrowserSentry'
import ErrorPage from './app/ErrorPage'
import Root from './app/Root'
import { FontProvider } from './context/font-provider'
import { ThemeProvider } from './context/theme-provider'
import './styles/index.css'
import { pdfjs } from 'react-pdf'

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString()

if (import.meta.env.VITE_CLARITY_PROJECT_ID) {
  clarity.init(import.meta.env.VITE_CLARITY_PROJECT_ID!)
  const storedUserId = sessionStorage.getItem(CLARITY_USER_ID_STORAGE_KEY)
  const storedArtemAdmin = sessionStorage.getItem(CLARITY_ARTEM_ADMIN_KEY)

  if (storedUserId && clarity.hasStarted()) {
    clarity.identify(storedUserId, {})
    if (storedArtemAdmin === 'true') {
      clarity.setTag('isArtemAdmin', 'true')
    }
  }
}

initBrowserSentry({
  surface: 'lex',
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
  replayEnabled: import.meta.env.VITE_SENTRY_REPLAY_ENABLED === 'true',
})

const queryClient = createQueryClientWithFailureReporting({
  auth: {
    kind: 'lex',
    sessionQueryKey: USER_SESSION_QUERY_KEY,
    signInPath: '/sign-in',
    authPaths: ['/sign-in', '/signup', '/sign-up', '/onboarding'],
  },
  queryDefaults: {
    retry: (failureCount, error) => {
      if (failureCount >= 0 && import.meta.env.DEV) {
        return false
      }
      if (failureCount > 3 && import.meta.env.PROD) {
        return false
      }
      return !(
        error instanceof AxiosError &&
        [401, 403].includes(error.response?.status ?? 0)
      )
    },
    refetchOnWindowFocus: import.meta.env.PROD,
    staleTime: 10 * 1000,
  },
})

const createBrowserRouterWithSentry =
  Sentry.wrapCreateBrowserRouterV6(createBrowserRouter)
const router = createBrowserRouterWithSentry([
  {
    path: '/*',
    element: <Root />,
    errorElement: <ErrorPage />,
  },
])

const rootElement = document.getElementById('root')!
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <FontProvider>
            <RouterProvider router={router} />
          </FontProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </StrictMode>
  )
}
